import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import parse from 'html-react-parser';
import Modal from '../Modal';
import useTrackEvent from '../../utils/trackEvent';
import type { Product as ProductType } from '../../types/product';

interface Props {
  product: ProductType;
}

const Product = ({ product }: Props) => {
  const trackEvent = useTrackEvent();
  const [modalIsOpen, setModalIsOpen] = useState(false);
  const url = window.customer?.library_endpoint ?? import.meta.env.VITE_LIBRARY_ENDPOINT;
  const disableBuy = window.customer?.disable_buy_button ?? false;

  if (!product) return null;

  const buyHref = disableBuy ? '#' : `https://www.thuisleefgids.nl/?puid=${product.uuid}`;
  const buyClass = (disableBuy ? 'btn-disabled ' : '') + 'btn-sm btn-primary';
  const isApp = product.categories?.map((c) => c?.slug).includes('apps');

  return (
    <>
      <div className="h-full flex flex-col sm:flex-row p-4 sm:p-0 bg-white sm:bg-none rounded-lg sm:rounded-none min-h-[174px]">
        <div className="flex-none h-[154px] w-[154px] overflow-hidden mx-auto sm:m-0 sm:pt-5">
          <img
            src={product.featured_image ?? '/assets/images/product-placeholder.png'}
            alt={product.name}
            className="h-full mx-auto"
          />
        </div>
        <div className="flex-1 p-5">
          <div className="flex flex-wrap items-center gap-5">
            <span className="font-bold">{product.name}</span>
            {product.isCompensated && (
              <span className="font-semibold text-sm inline-flex gap-2 items-center rounded-full py-1 px-2.5 border-2 border-secondary-orange text-secondary-orange">
                <i className="fa-solid fa-check" />
                Vergoed
              </span>
            )}
          </div>
          {product.short_description && (
            <div className="mt-3 lg:mt-1 text-sm lg:text-lg">{parse(product.short_description)}</div>
          )}
          <button onClick={() => setModalIsOpen(!modalIsOpen)} className="inline-flex items-center gap-2.5 mt-3 lg:mt-5">
            <i className="fas fa-chevron-right text-secondary-orange" />
            <span className="font-bold text-sm">Meer informatie</span>
          </button>
          <div className="mt-5 flex flex-wrap items-center gap-2.5">
            <Link to={buyHref} className={buyClass} target="_blank"
              onClick={() => trackEvent('Click_ViewProductGids', { select: product.name, product_id: product.id, product_uuid: product.uuid })}>
              Kopen
            </Link>
            {product.exists && url && (
              <Link to={`${url}?puid=${product.uuid}`} className="btn-sm btn-primary-outline" target="_blank"
                onClick={() => trackEvent('Click_ViewProductBieb', { select: product.name, product_id: product.id, product_uuid: product.uuid })}>
                {isApp ? 'Download app' : 'Lenen'}
              </Link>
            )}
            {!product.exists && !product.uuid && (
              <Link to="#" className="btn-sm btn-primary-outline">Niet beschikbaar</Link>
            )}
          </div>
        </div>
      </div>
      <Modal show={modalIsOpen} handleClose={() => setModalIsOpen(!modalIsOpen)}>
        <div className="max-w-[600px] p-6 pb-0">
          {product.description && <div>{parse(product.description)}</div>}
        </div>
        {product.attributes && product.attributes.length > 0 && (
          <div className="max-w-[600px] p-6 pb-0">
            <h4>Specificaties</h4>
            <div className="grid grid-cols-2 gap-2 mt-2">
              {product.attributes.map((item) => (
                <div key={item.id}>
                  <span className="font-bold">{item.name}:</span>
                  <span> {item.options[0]}</span>
                </div>
              ))}
            </div>
          </div>
        )}
        <div className="flex flex-wrap items-center gap-2.5 p-6">
          <Link to={buyHref} className={buyClass} target="_blank"
            onClick={() => trackEvent('Click_ViewProductGids', { select: product.name, product_id: product.id, product_uuid: product.uuid })}>
            Kopen
          </Link>
          {product.exists && url && (
            <Link to={`${url}?puid=${product.uuid}`} className="btn-sm btn-primary-outline" target="_blank"
              onClick={() => trackEvent('Click_ViewProductBieb', { select: product.name, product_id: product.id, product_uuid: product.uuid })}>
              {isApp ? 'Download app' : 'Lenen'}
            </Link>
          )}
          {!product.exists && !product.uuid && (
            <Link to="#" className="btn-sm btn-primary-outline">Niet beschikbaar</Link>
          )}
        </div>
      </Modal>
    </>
  );
};

export default Product;
